import { useEffect, useState } from "react";
import { Check, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useApp } from "@/context/AppContext";
import { fmtNum, round2 } from "@/lib/format";
import { NumPad } from "@/components/NumPad";
import { BigButton } from "@/components/ui-kit";
import CustomerPicker from "@/components/customers/CustomerPicker";
import { saleFrom } from "@/components/pos/CheckoutDialog";

function Part({ label, value, active, onClick, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`rounded-xl border-2 p-2 text-start ${active ? "border-[#0B2A5B] bg-[#EEF5FB]" : "border-border bg-white"}`}>
      <div className="text-xs font-bold text-muted-foreground">{label}</div>
      <div className="text-2xl font-extrabold" dir="ltr">{value || "0"}</div>
    </button>
  );
}

export default function SplitDialog({ open, onClose, cart, totals, promo = null, onDone, initialCustomer = null }) {
  const { t, money, dispatch, staff, view } = useApp();
  const [f, setF] = useState({ cash: "", debt: "" });
  const [field, setField] = useState("cash");
  const [customerId, setCustomerId] = useState(null);
  useEffect(() => { if (open) { setF({ cash: "", debt: "" }); setField("cash"); setCustomerId(initialCustomer); } }, [open, initialCustomer]);
  const total = totals.total;
  const card = 0;
  const debt = round2(Math.max(0, Number(f.debt || 0)));
  const given = Number(f.cash || 0);
  const cash = round2(Math.max(0, Math.min(given, total - debt)));
  const credit = round2(Math.max(0, total - cash));
  const change = round2(Math.max(0, given - cash));
  const customer = customerId ? view.customers[customerId] : null;
  const overLimit = customer && customer.credit_limit > 0 && customer.balance + credit > customer.credit_limit;
  const valid = debt <= total && (credit <= 0 || !!customer);
  const rest = (k) => setF((x) => ({ ...x, [k]: String(round2(Math.max(0, total - Number(x[k === "debt" ? "cash" : "debt"] || 0)))) }));

  const confirm = () => {
    const sale = { ...saleFrom({ cart, totals, promo, mode: "split", paid: round2(cash + card), given: change > 0 ? round2(total + change) : null, customer }), split: { cash, card, credit } };
    if (!dispatch("sale.create", sale)) return;
    toast.success(t("sale_saved"));
    onDone({ ...sale, staff_name: staff.name, created_at: new Date().toISOString(), balance_after: customer ? round2(customer.balance + credit) : null });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[96vh] overflow-y-auto p-4" data-testid="split-dialog">
        <DialogTitle className="font-heading text-xl flex items-center justify-between gap-3">{t("split_title")}<span className="text-2xl font-extrabold" data-testid="split-total">{money(total)}</span></DialogTitle>
        <div className="grid md:grid-cols-2 gap-3">
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <Part label={t("split_cash")} value={f.cash} active={field === "cash"} onClick={() => setField("cash")} testid="split-cash-field" />
              <Part label={t("split_debt")} value={f.debt} active={field === "debt"} onClick={() => setField("debt")} testid="split-debt-field" />
              <button type="button" onClick={() => rest("cash")} className="h-9 rounded-lg border border-border text-xs font-bold" data-testid="split-rest-cash">{t("split_rest_cash")}</button>
              <button type="button" onClick={() => rest("debt")} className="h-9 rounded-lg border border-border text-xs font-bold" data-testid="split-rest-debt">{t("split_rest_debt")}</button>
            </div>
            <NumPad value={f[field]} onChange={(v) => setF((x) => ({ ...x, [field]: v }))} testid="split-numpad" compact />
          </div>
          <div className="space-y-2 flex flex-col">
            <div className="rounded-xl bg-[#EEF5FB] p-3 text-sm font-bold space-y-0.5" data-testid="split-summary">
              <div className="flex justify-between"><span>{t("pay_cash")}</span><span dir="ltr" data-testid="split-sum-cash">{fmtNum(cash)}</span></div>
              <div className="flex justify-between text-[#A85A12]"><span>{t("split_debt")}</span><span dir="ltr" data-testid="split-sum-credit">{fmtNum(credit)}</span></div>
              {change > 0 && <div className="flex justify-between text-[#1F9D55]"><span>{t("change")}</span><span dir="ltr" data-testid="split-change">{fmtNum(change)}</span></div>}
              {debt > total && <div className="text-destructive" data-testid="split-debt-over">{t("split_debt_over")}</div>}
            </div>
            {credit > 0 && (
              <div className="space-y-2 flex-1">
                <div className="text-sm font-bold">{t("split_need_customer")}</div>
                <CustomerPicker value={customerId} onChange={setCustomerId} />
                {overLimit && <div className="rounded-xl p-3 text-sm font-bold bg-destructive/10 text-destructive" data-testid="split-over-limit"><AlertTriangle className="inline w-4 h-4 me-1" />{t("over_limit")} ({money(customer.credit_limit)})</div>}
              </div>
            )}
            <BigButton variant="green" disabled={!valid} onClick={confirm} className="w-full mt-auto" data-testid="split-confirm-btn"><Check className="w-6 h-6" />{t("confirm_sale")}</BigButton>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
